import { useTheme } from '@emotion/react'
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { isWeekend, addDays, startOfDay, isBefore } from 'date-fns'
import { Dialog } from '~/components/Dialog'
// import { Input } from '~/components/Form/Input'
import { Checkbox } from '~/components/Form/Checkbox'
import { ProviderInvoiceSimulateDueDateChangeQuery, useProviderInvoiceSimulateDueDateChangeLazyQuery, useProviderInvoiceChangeDueDateMutation } from '~/graphql/types'
import { formatMoney, transformMoney } from '~/utils'
import ptBR from 'date-fns/locale/pt-BR'

type SecondInvoiceGenerateModalProps = {
  onClose: Function
  isVisible: boolean
  invoiceId: null | string
  contractId: null | string
  onSuccess?: Function
}

type FormData = {
  dueDate: Date | null
  isInterestAndFineWaived: boolean
}

// Função para obter a data mínima (a partir de amanhã)
const getMinDate = () => {
  const today = startOfDay(new Date()) // Normaliza para início do dia
  return addDays(today, 1) // Permite a partir de amanhã, shouldDisableDate cuida dos fins de semana
}

// Schema de validação
const validationSchema = yup.object().shape({
  dueDate: yup
    .date()
    .required('Data de vencimento é obrigatória')
    .test('min-date', 'A data de vencimento deve ser a partir de amanhã', (value) => {
      if (!value) return true
      const minDate = getMinDate()
      const normalizedValue = startOfDay(value)
      const normalizedMinDate = startOfDay(minDate)
      // Aceita a partir de amanhã (inclusive)
      return !isBefore(normalizedValue, normalizedMinDate)
    })
    .test('no-weekend', 'A data de vencimento não pode ser em final de semana', (value) => {
      if (!value) return true
      return !isWeekend(value)
    }),
  isInterestAndFineWaived: yup.boolean().required()
})

type selectedDueDateType = ProviderInvoiceSimulateDueDateChangeQuery['providerInvoiceSimulateDueDateChange']

export const SecondInvoiceGenerateModal: React.FC<SecondInvoiceGenerateModalProps> = ({ isVisible, onClose, invoiceId, contractId, onSuccess }) => {
  const theme = useTheme()
  const formMethods = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      dueDate: getMinDate(),
      isInterestAndFineWaived: true
    }
  })
  const { dueDate: watchDueDate, isInterestAndFineWaived: watchIsInterestAndFineWaived } = useWatch({ control: formMethods.control })
  const { isValid } = formMethods.formState
  const [selectedDueDate, setSelectedDueDate] = useState<null | selectedDueDateType>(null)

  const onCloseModal = useCallback(() => {
    onClose()
    setSelectedDueDate(null)
    formMethods.reset()
  }, [])

  const [simulateDueDateChange, { loading: loadingCalculateNewDueDate }] = useProviderInvoiceSimulateDueDateChangeLazyQuery({
    onCompleted(data) {
      if (data?.providerInvoiceSimulateDueDateChange) {
        setSelectedDueDate(data.providerInvoiceSimulateDueDateChange)
      }
    },
    onError(error) {
      toast.error(error.message)
      onCloseModal()
    },
  })

  const [changeDueDate, { loading: changeDueDateIsLoading }] = useProviderInvoiceChangeDueDateMutation({
    onCompleted() {
      toast.success('Solicitação recebida! Estamos gerando sua 2ª via.')
      onCloseModal()
      onSuccess?.() // Chama o callback para recarregar a lista
    },
    onError(error) {
      toast.error(error.message)
    },
  })

  useEffect(() => {
    // Define data padrão como amanhã e checkbox marcado quando o modal abre
    if (isVisible && invoiceId) {
      formMethods.setValue('dueDate', getMinDate())
      formMethods.setValue('isInterestAndFineWaived', true)
    }
  }, [isVisible, invoiceId, formMethods])

  useEffect(() => {
    if (watchDueDate && invoiceId) {
      simulateDueDateChange({
        variables: {
          params: {
            newDueDate: watchDueDate,
            invoiceId: invoiceId || '',
            isInterestAndFineWaived: !watchIsInterestAndFineWaived
          }
        }
      })
    }
  }, [watchDueDate, watchIsInterestAndFineWaived, invoiceId])

  const onSubmit = useCallback((data: FormData) => {
    changeDueDate({
      variables: {
        params: {
          newDueDate: data.dueDate,
          invoiceId: invoiceId || '',
          contractId: contractId || '',
          isInterestAndFineWaived: !data.isInterestAndFineWaived
        }
      }
    })

  }, [invoiceId, contractId])

  return (
    <Dialog loading={loadingCalculateNewDueDate} onClose={() => onCloseModal()} title='Gerar 2ª via de fatura' isVisible={isVisible}>

      <FormProvider {...formMethods}>
        <Divider sx={{ margin: '.4rem 0 1.4rem 0' }} />
        <form onSubmit={formMethods.handleSubmit((data) => onSubmit(data))}>
          <Box sx={{ display: 'flex', flexFlow: 'column', gap: '1rem' }}>
            <Typography>
              Selecione a data de vencimento da 2ª via:
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <Controller
                name='dueDate'
                control={formMethods.control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    label='Data de vencimento da 2ª via'
                    shouldDisableDate={isWeekend}
                    minDate={getMinDate()}
                    slotProps={{
                      textField: {
                        error: !!error,
                        helperText: error?.message,
                        fullWidth: true
                      }
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            <Checkbox
              name='isInterestAndFineWaived'
              label='Cobrar juros e multa'
              defaultValue={true}
            />
            <Box sx={{ display: 'flex', flexFlow: 'column', gap: '.2rem' }}>
              <Typography sx={{ color: theme.palette.grey[800] }}>O novo valor da fatura será de <Typography sx={{ fontWeight: 500, display: 'inline' }}>
                {formatMoney(transformMoney(selectedDueDate?.amountCents || 0, 'toReal'))}</Typography>
              </Typography>
              {watchIsInterestAndFineWaived && (
                <Typography sx={{ color: theme.palette.grey[600], fontWeight: 500 }}>
                  ({formatMoney(transformMoney((selectedDueDate?.fineAmountCents || 0) + (selectedDueDate?.interestAmountCents || 0), 'toReal'))} de multas e juros)
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ margin: '1.4rem' }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem' }}>
            <Button onClick={() => onCloseModal()} color='secondary'>Cancelar</Button>
            <Button
              type='submit'
              disabled={!isValid || changeDueDateIsLoading}
              endIcon={changeDueDateIsLoading ? <CircularProgress size={24} color='inherit' /> : null}
            >
              Gerar 2ª via
            </Button>
          </Box>
        </form>
      </FormProvider>

    </Dialog>
  )
}
